import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { Trans, useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { theme } from '@frontend/theme';
import { Text, useModalControl } from '@frontend/design-system';
import { useCallIntelDefaultsShallowStore } from '../../hooks/use-call-intel-defaults';
import { InfoTooltipModal } from '../modal/info-tooltip-modal';

type Data = {
  modalContent?: ReactNode;
  noTipContentInModal?: boolean;
  tipContent: ReactNode | string;
  title: string;
};

type Props = {
  customTipTitle?: string;
  noCta?: boolean;
  showCallIntelPrefix?: boolean;
  tip: CallIntelTypes.InfoTipId;
};

export const CallIntelTooltip = ({ customTipTitle, noCta, showCallIntelPrefix = false, tip }: Props) => {
  const { t } = useTranslation('analytics');
  const {
    patient: patientToken,
    patientPlural: patientPluralToken,
    patientPossessive: patientPossessiveToken,
    patientCapitalizedPlural: patientCapitalizedPluralToken,
    nonPatientCapitalizedPlural: nonPatientCapitalizedPluralToken,
    nonPatient: nonPatientToken,
  } = useTranslationTokens();
  const { modalProps, triggerProps } = useModalControl();
  const { dataLabels } = useCallIntelDefaultsShallowStore('dataLabels');

  const data: Record<CallIntelTypes.InfoTipId, Data> = {
    callsAnalyzed: {
      title: t('Calls Analyzed'),
      tipContent: t(
        'This is the total number of successfully analyzed calls for the selected location(s) and time period. We might be unable to analyze some calls due to poor audio quality, very short call duration, or inability to meet our safety guidelines.'
      ),
      modalContent: (
        <>
          <Text>{t('Each successfully analyzed call will have a:')}</Text>
          <ul>
            <li>{t('Transcript')}</li>
            <li>{t('Summary')}</li>
            <li>{t('Scheduling Opportunity')}</li>
            <li>{t('Scheduling Outcome (if applicable)')}</li>
            <li>{t('Customer Sentiment')}</li>
            <li>{t('Appointment Type (if applicable)')}</li>
            <li>{t('Category')}</li>
          </ul>
        </>
      ),
    },

    callsByAppointmentType: {
      title: t('Calls by Appointment Type'),
      tipContent: t(
        'Appointment Types are created by Weave for different services, such as treatments or prescriptions, mentioned during a call. These can be brought up by either the {{patientToken}} or the staff member. A call may include one, several, or no Appointment Types.',
        { patientToken }
      ),
      modalContent: (
        <Text>
          {t(
            'Calls by Appointment Type tells you how many calls each Appointment Type is mentioned in for the given location(s) and time period.'
          )}
        </Text>
      ),
    },

    callsByCategory: {
      title: t('Calls by Category'),
      tipContent: t(
        'Categories are created by Weave to classify the nature of conversation, or issues mentioned during a call. A call may have one or several Categories.'
      ),
      modalContent: (
        <Text>
          {t(
            'Calls by Category tells you how many calls are in each Category for the given location(s) and time period.'
          )}
        </Text>
      ),
    },

    callsToReview: {
      title: t('Calls to Review'),
      tipContent: t(
        'Calls to Review are selected as requiring your attention due to potential {{patientToken}} issues and dissatisfaction. By reviewing these calls, you can address {{patientToken}} concerns and unmet needs, helping to ensure a more positive experience with your office.',
        { patientToken }
      ),
    },

    callEscalationServiceIssue: {
      title: t('Manager Escalations'),
      tipContent: t(
        'A call is marked as a Manager Escalation when a {{patientToken}} asks to speak with a manager or suggests a higher-up should be involved.',
        { patientToken }
      ),
    },

    contactType: {
      title: t('Contact Type'),
      tipContent: (
        <Trans t={t} values={{ patientCapitalizedPluralToken, nonPatientCapitalizedPluralToken }}>
          <Text>
            New and Existing {patientCapitalizedPluralToken} are determined by appointment data from your integration or
            from Weave’s Schedule.
          </Text>

          <Text css={styles.withMargin}>
            <strong>New {patientCapitalizedPluralToken}:</strong> Individuals with no completed appointments at the time
            of the call.
          </Text>

          <Text css={styles.withMargin}>
            <strong> Existing {patientCapitalizedPluralToken}:</strong> Individuals who have had at least one completed
            appointment at the time of the call.
          </Text>

          <Text css={styles.withMargin}>
            <strong> {nonPatientCapitalizedPluralToken}:</strong> Callers identified as Insurance Representative, IT
            Support Representative, Job Applicant, Medical Office Representative, Medical Student, Pharmacy
            Representative, Sales Solicitor, or Vendor Representative.
          </Text>
        </Trans>
      ),
      modalContent: (
        <Text>
          {t(
            'Call Intelligence prioritizes {{patientToken}}-related insights. For {{nonPatientToken}} calls, it provides only the Summary, Transcript, Tasks, Task Reasons, and Category, helping you focus on improving {{patientToken}} satisfaction and booking missed scheduling opportunities.',
            {
              patientToken,
              nonPatientToken,
            }
          )}
        </Text>
      ),
    },

    conversionRate: {
      title: t('Scheduled'),
      tipContent: t(
        'Scheduled Rate is an indicator of the overall performance of your location(s). It tells you the percentage of Scheduling Opportunities that are successfully converted into scheduled appointments.'
      ),
      modalContent: (
        <Text className='with-margin'>
          {t(
            'A call is identified as a Scheduling Opportunity if there was potential to schedule an appointment during the call. Not all analyzed calls may be opportunities. If there was an opportunity and an appointment was scheduled during the call, it will be marked as Scheduled.'
          )}
        </Text>
      ),
    },

    excellentService: {
      title: t('Excellent Service'),
      tipContent: (
        <>
          <Trans t={t} values={{ patientToken }}>
            <Text>
              A call is marked as Excellent Service when <strong> one or more </strong> of the following actions
              exemplifying effective {patientToken} interactions are taken:
            </Text>
          </Trans>
          <Text>
            <ul>
              <li>
                {t(
                  "Offering alternative options when there's a conflict or the {{patientToken}} declines an initial solution",
                  { patientToken }
                )}
              </li>
              <li>
                {t(
                  'Providing clear information to help the {{patientToken}} understand treatment or procedure details',
                  { patientToken }
                )}
              </li>
              <li>{t('Giving instructions after scheduling an appointment')}</li>
              <li>{t('Addressing all financial concerns')}</li>
            </ul>
          </Text>
        </>
      ),
      modalContent: (
        <Text className='with-margin'>
          {t(
            'Call Intelligence will continue expanding the criteria to include the most relevant and exemplary instances of Excellent Service.'
          )}
        </Text>
      ),
    },

    followUpReason: {
      title: t('Follow-up Reason'),
      tipContent: t(
        'A Follow-up Reason categorizes the type of Task generated from a call. A call may have no Tasks and no Follow-up Reasons if no open action items were identified in the transcript of the call.'
      ),
    },

    followUpSummary: {
      title: t('Follow-up Tasks by Reason'),
      tipContent: t(
        'Follow-up Tasks group tasks by their assigned Follow-up Reason, helping you focus on related tasks. For example, the Scheduling list lets you manage all scheduling-related tasks for {{patientPluralToken}} within a specific time frame. On the All Follow-ups page, you can view all tasks across all Follow-up Reasons for any selected time frame.',
        {
          patientPluralToken,
        }
      ),
      modalContent: (
        <>
          <Text>
            {t(
              'A Task is generated by Call Intelligence AI based on the contents of a conversation. It represents an open action item that needs to be addressed by an office staff member after the call ends.'
            )}
          </Text>
        </>
      ),
    },

    ineffectiveTreatmentServiceIssue: {
      title: t('Treatment Concerns'),
      tipContent: t(
        'A call is marked as a Treatment Concern when a {{patientToken}} expresses concern about treatment provided by your office during the call.',
        { patientToken }
      ),
    },

    repeatCallerServiceIssue: {
      title: t('Unresolved Contact Attempts'),
      tipContent: t(
        'A call is marked as an Unresolved Contact Attempt when a {{patientToken}} mentions trying to reach the office multiple times or not receiving an expected callback.',
        { patientToken }
      ),
    },

    schedulingOpportunities: {
      title: t('Scheduling Opportunities'),
      tipContent: t(
        'A call is identified as a Scheduling Opportunity if there was potential to schedule an appointment during the call. Not all analyzed calls may be Scheduling Opportunities.'
      ),
    },

    schedulingOutcome: {
      title: t('Scheduling Outcome'),
      tipContent: t(
        'For a Scheduling Opportunity, if an appointment was scheduled during the call, the opportunity will be marked as Scheduled. If not, it will be marked as Unscheduled. Calls with no Scheduling Opportunity will not have a Scheduling Outcome.'
      ),
    },

    schedulingOutcomeV2: {
      title: t('Scheduling Outcome'),
      tipContent: (
        <>
          <Text>
            {t(
              'Call Intelligence analyzes each call to determine if there was an opportunity to schedule the {{patientToken}} and what happened next.',
              { patientToken }
            )}
          </Text>

          <ul css={styles.withTipContentList}>
            <li>
              <Text size='medium' weight='bold'>
                {
                  dataLabels.schedulingOutcomeV2?.[
                    CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL
                  ]
                }
                :{' '}
                <Text as='span' size='medium' weight='regular'>
                  {t('The {{patientToken}} booked an appointment during the call.', { patientToken })}
                </Text>
              </Text>
            </li>
            <li>
              <Text size='medium' weight='bold'>
                {
                  dataLabels.schedulingOutcomeV2?.[
                    CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED
                  ]
                }
                :{' '}
                <Text as='span' size='medium' weight='regular'>
                  {t('There was an opportunity to schedule, but the {{patientToken}} did not book.', { patientToken })}
                </Text>
              </Text>
            </li>
            <li>
              <Text size='medium' weight='bold'>
                {
                  dataLabels.schedulingOutcomeV2?.[
                    CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
                  ]
                }
                :{' '}
                <Text as='span' size='medium' weight='regular'>
                  {t(
                    'For every Unscheduled call, Call Intelligence checks if an appointment was created within 7 days after the call. If so, the call is automatically updated to Recovered Opportunity and is no longer considered an Active Unscheduled Opportunity.*'
                  )}
                </Text>
              </Text>
            </li>
            <li>
              <Text size='medium' weight='bold'>
                {
                  dataLabels.schedulingOutcomeV2?.[
                    CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NO_OPPORTUNITY
                  ]
                }
                :{' '}
                <Text as='span' size='medium' weight='regular'>
                  {t(
                    'The call was not about scheduling an appointment (e.g., billing, insurance, or other inquiries).'
                  )}
                </Text>
              </Text>
            </li>
          </ul>

          <Text
            size='medium'
            css={css`
              margin-top: ${theme.spacing(2)};
              font-style: italic;
            `}
          >
            *Only available for certain integrations.
          </Text>
          <Text size='medium'>
            You can also manually mark Unscheduled calls as Recovered Opportunity to keep your list accurate.
          </Text>
        </>
      ),
    },

    sentiments: {
      title: t('Customer Sentiment'),
      tipContent: t(
        'Customer Sentiment types are defined by Weave to show the demeanor of participants during the call. They are determined by factors such as the choice of words and the context of the conversation.'
      ),
      modalContent: (
        <>
          <Text>{t('There are 4 sentiment types:')}</Text>
          <ul>
            <li>{dataLabels.sentimentsWithEmoji?.[CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE]}</li>
            <li>{dataLabels.sentimentsWithEmoji?.[CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL]}</li>
            <li>{dataLabels.sentimentsWithEmoji?.[CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE]}</li>
            <li>
              {t('{{value}} - for any call sentiment the AI model is unable to confidently detect', {
                value: dataLabels.sentimentsWithEmoji?.[CallIntelTypes.SentimentEnum.SENTIMENT_UNKNOWN],
              })}
            </li>
          </ul>
          <Text className='with-margin'>
            {t(
              'Customer Sentiment breaks down all analyzed calls by the different sentiment types for the given location(s) and time period.'
            )}
          </Text>
        </>
      ),
    },

    serviceIssues: {
      title: t('Service Issues'),
      tipContent: t(
        'Service Issues detect potential gaps in customer or clinical service that are raised by the patient during the call, such as concerns about treatment, unresolved attempts to connect with the office, or requests to escalate an issue to a manager.'
      ),
      modalContent: (
        <>
          <Text>
            {t(
              'By identifying these issues, your team can take timely action to improve the patient experience and resolve operational gaps before they lead to dissatisfaction or churn.'
            )}
          </Text>
          <Text css={styles.withMargin}>
            {t(
              'Service Issues may not capture every type of service-related problem. As Call Intelligence evolves, more issue types may be identified and surfaced.'
            )}
          </Text>
        </>
      ),
    },

    serviceQuality: {
      title: t('Service Quality'),
      tipContent: t(
        'Service Quality empowers you to elevate customer service and enhance {{patientToken}} satisfaction. By identifying notable Calls to Review and Spotlighted Calls, you can focus on addressing potential {{patientToken}} issues while highlighting effective practices that enrich {{patientToken}} interactions.',
        { patientToken }
      ),
    },

    serviceQualityCallVolume: {
      title: t('Service Quality Call Volume'),
      tipContent: (
        <>
          <Text>
            {t(
              'This graph displays the volume of Service Quality categories over the selected time period, comparing it to the overall volume of Calls Analyzed.'
            )}
          </Text>

          <Trans t={t}>
            <Text
              css={css`
                margin-top: ${theme.spacing(2)};
              `}
            >
              Note that the number of Calls Analyzed <strong>does not include</strong> missed calls, outbound
              voicemails, and calls without an analysis (short duration calls and failed calls). This number will differ
              from the call volume in Phone Analytics.
            </Text>
          </Trans>
        </>
      ),

      modalContent: (
        <>
          <Text>
            {t(
              'This graph displays the volume of Service Quality categories over the selected time period, comparing it to the overall volume of Calls Analyzed.'
            )}
          </Text>
          <Text className='with-margin'>
            {t(
              'This comparison helps you identify how fluctuations in call volume or different times, such as time of day or week, may affect Service Quality.'
            )}
          </Text>
          <Trans t={t}>
            <Text className='with-margin'>
              Note that the number of Calls Analyzed <strong>does not include</strong> missed calls, outbound
              voicemails, and calls without an analysis (short duration calls and failed calls). This number will differ
              from the call volume in Phone Analytics.
            </Text>
          </Trans>
        </>
      ),
      noTipContentInModal: true,
    },

    serviceQualityPeriodOverPeriod: {
      title: t('Service Quality Period-Over-Period'),
      tipContent: t(
        'This chart helps you assess whether your Service Quality rates are improving by comparing the selected time period with the previous one.'
      ),
      modalContent: (
        <>
          <Text>
            {t(
              'For ongoing periods such as “This Week” that contain the current day, the data reflects the current state and will continue to be updated. Since the period is still in progress, a full comparison will be most accurate once the time period is complete. '
            )}
          </Text>
          <Text className='with-margin'>
            {t(
              'Previous periods may have limited data if Call Intelligence or new features were not active during that time.'
            )}
          </Text>
        </>
      ),
    },

    spotLightedCalls: {
      title: t('Spotlighted Calls'),
      tipContent: t(
        'Spotlighted Calls highlight best practices that elevate {{patientToken}} interactions. These calls showcase instances where staff members took effective steps to address the {{patientPossessiveToken}} needs, even if those needs could not be fully met.',
        {
          patientToken,
          patientPossessiveToken,
        }
      ),
      modalContent: (
        <Text>{t('In some cases, a call can be spotlighted even if it is identified as a Call to Review.')}</Text>
      ),
    },

    tasks: {
      title: t('Tasks'),
      tipContent: t(
        'A Task is generated by Call Intelligence AI based on the contents of a conversation. It represents an action item that needs to be addressed by an office staff member after the call ends. Manually update the status of a Task to indicate when it is in progress or completed.'
      ),
    },

    taskTypeBilling: {
      title: t('Billing'),
      tipContent: t(
        'Billing follow-ups include tasks such as discussing treatment costs, managing payment information, and sending receipts and statements.'
      ),
    },

    taskTypeInsurance: {
      title: t('Insurance'),
      tipContent: t(
        'Insurance follow-ups include tasks such as verifying insurance coverage and contacting insurance companies.'
      ),
    },

    taskTypeOther: {
      title: t('Other'),
      tipContent: t(
        'Other follow-ups are tasks that are not {{patientToken}}-related and do not fall under the other 5 categories of Follow-up Reasons.',
        {
          patientToken,
        }
      ),
    },

    taskTypePatientCare: {
      title: t('Patient Care'),
      tipContent: t(
        'Patient Care follow-ups include tasks such as sending new {{patientToken}} paperwork, contacting {{patientPluralToken}} to discuss treatment plans and options, refilling prescriptions, discussing lab results, updating medical records, sending referrals, and other treatment-related tasks.',
        {
          patientToken,
          patientPluralToken,
        }
      ),
    },

    taskTypeScheduling: {
      title: t('Scheduling'),
      tipContent: t(
        'Scheduling follow-ups include tasks such as contacting {{patientPluralToken}} to schedule an appointment, sending appointment confirmations and reminders, and canceling appointments. These are identified by Call Intelligence AI as tasks that need to be completed.',
        {
          patientPluralToken,
        }
      ),
    },

    taskTypeWaitlist: {
      title: t('Waitlist'),
      tipContent: t(
        'Waitlist follow-up tasks are for adding {{patientPluralToken}} to the ASAP list or Waitlist to get them scheduled sooner in case of a cancellation. This helps keep your office’s appointment calendar full at all times.',
        {
          patientPluralToken,
        }
      ),
    },

    unresolved: {
      title: t('Unresolved'),
      tipContent: (
        <>
          <Text>
            {t(
              "A call is marked as Unresolved when the {{patientPossessiveToken}} issue isn't fully addressed during the conversation, whether due to waiting on a third party, needing further action, or {{patientToken}} dissatisfaction without resolution.",
              {
                patientPossessiveToken,
                patientToken,
              }
            )}
          </Text>
          <Text style={{ marginTop: theme.spacing(2) }}>
            {t(
              'It can also be considered Unresolved if the {{patientToken}} made multiple calls about the same issue or couldn’t reach the right person.',
              {
                patientToken,
              }
            )}
          </Text>
          <Text style={{ marginTop: theme.spacing(2) }}>
            {t('Unresolved calls may involve Unscheduled opportunities or incomplete Tasks.')}
          </Text>
        </>
      ),
    },

    unscheduledOpportunitiesByLocation: {
      title: t('Unscheduled Opportunities by Location'),
      tipContent: t(
        'Unscheduled Opportunities by Location tells you how many appointments were not scheduled on a call for the given locations and time period.'
      ),
      modalContent: (
        <>
          <Text>
            {t(
              'A call is identified as a Scheduling Opportunity if there was potential to schedule an appointment during the call. Not all analyzed calls may be Scheduling Opportunities.'
            )}
          </Text>
          <Text className='with-margin'>
            {t(
              'For a Scheduling Opportunity, if an appointment was not scheduled during the call, the opportunity will be marked as Unscheduled.'
            )}
          </Text>
          <Text className='with-margin'>
            {t(
              'Unscheduled Opportunities by Location tells you how many appointments were not scheduled on a call for the given locations and time period.'
            )}
          </Text>
        </>
      ),
      noTipContentInModal: true,
    },

    activeOpportunities: {
      title: t('Active Opportunities'),
      tipContent: (
        <div css={styles.container}>
          <Trans t={t} values={{ patientToken, patientPluralToken }}>
            <div>
              <Text size='medium' weight='bold'>
                What It Helps With
              </Text>
              <Text size='medium'>
                Focus your follow-up efforts by seeing which {{ patientPluralToken }} still need to be scheduled,
                helping you turn missed opportunities into appointments and revenue.
              </Text>
            </div>
            <div>
              <Text size='medium' weight='bold'>
                How It's Defined
              </Text>
              <Text size='medium'>
                Out of all Unscheduled calls ({{ patientToken }} did not schedule during the call), we check if an
                appointment was created for the {{ patientToken }} within 7 days after the call.*
              </Text>
            </div>
            <ul css={styles.list}>
              <Text as='li' size='medium'>
                <strong>Still Unscheduled:</strong> No appointment created within 7 days. Call remains in the
                Unscheduled list
              </Text>

              <Text as='li' size='medium'>
                <strong>Recovered Opportunity:</strong> Appointment created within 7 days. Opportunity is likely
                recovered and converted
              </Text>
            </ul>
            <div>
              <Text size='medium' css={{ fontStyle: 'italic' }}>
                *Only available for certain integrations.
              </Text>
              <Text size='medium'>
                You can also manually mark Unscheduled calls as Recovered Opportunity to keep your list accurate.
              </Text>
            </div>
          </Trans>
        </div>
      ),
    },

    conversionDuringCall: {
      title: t('Conversion During Call'),
      tipContent: (
        <div css={styles.container}>
          <Trans t={t} values={{ patientToken, patientPluralToken }}>
            <div>
              <Text size='medium' weight='bold'>
                What It Helps With
              </Text>
              <Text size='medium'>
                Track your scheduling performance during calls to see how often opportunities are booked on the spot,
                helping you identify coaching opportunities to get more {{ patientPluralToken }} scheduled during
                conversations.
              </Text>
            </div>
            <div>
              <Text size='medium' weight='bold'>
                How It's Defined
              </Text>
              <Text size='medium'>
                Out of all Opportunities (calls where there was an opportunity to schedule the {{ patientToken }}):
              </Text>
            </div>
            <ul css={styles.list}>
              <Text as='li' size='medium'>
                <strong>Scheduled Rate (%):</strong> A key measure of your scheduling performance during calls.
              </Text>
              <Text as='li' size='medium'>
                <strong>Scheduled On Call:</strong> The {{ patientToken }} booked an appointment at the time of the
                call.
              </Text>
              <Text as='li' size='medium'>
                <strong>Unscheduled On Call:</strong> No appointment was booked at the time of the call.
              </Text>
            </ul>
          </Trans>
        </div>
      ),
    },
  };

  return (
    <InfoTooltipModal
      ctaAction={noCta || !data[tip]?.modalContent ? undefined : triggerProps.onClick}
      modalContent={data[tip]?.modalContent}
      modalProps={modalProps}
      noTipContentInModal={data[tip]?.noTipContentInModal}
      showCallIntelPrefix={showCallIntelPrefix}
      tipContent={data[tip]?.tipContent}
      title={customTipTitle || data[tip]?.title}
      trackingId={tip}
    />
  );
};

const styles = {
  withMargin: css`
    margin-top: ${theme.spacing(3)};
  `,
  list: css`
    padding-inline-start: ${theme.spacing(3)};
  `,
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  withTipContentList: css`
    margin-top: ${theme.spacing(2)};
    list-style: disc;
    padding: ${theme.spacing(0, 3)};
  `,
};
